import * as React from "react";
import { Fun, Action, IOWidget, mk_widget, Option, option, Options } from "../widget";

export interface StateOptions<s> extends Options {
  mergeNewProps?:(currentState:s, propsState:s) => s,
  onKeyDown?:Fun<s,Fun<KeyboardEvent,Option<s>>>
}

interface StatefulProps<s> {
  options?:StateOptions<s>
  input:s
  widget_s:IOWidget<s,s>
  on_done_s:(output_s:s) => void
}
interface StatefulState<s> {
  value:s
}

/**
 * @deprecated Don't use directly, use the [[stateful]] function instead.
 */
class Stateful<s> extends React.Component<StatefulProps<s>, StatefulState<s>> {
  constructor(props:StatefulProps<s>, context:any) {
    super(props, context)

    this.state = { value:props.input }
  }

  onKeyDown?:Fun<KeyboardEvent,any>
  registerKeyDown() {
    if (this.onKeyDown) window.removeEventListener("keydown", this.onKeyDown)
    if (this.props.options != undefined && this.props.options.onKeyDown != undefined) {
      const onKeyDown = this.props.options.onKeyDown
      this.onKeyDown = e => {
        let res = onKeyDown!(this.state.value)(e)
        if (res.v.kind == "r") {
          e.preventDefault()
          this.on_new_state(res.v.v)
          return false
        }
        return true
      }
      window.addEventListener("keydown", this.onKeyDown)
    }
  }

  componentDidMount() {
    this.registerKeyDown()
    this.on_new_state(this.state.value)
  }

  componentDidUpdate() {
    this.registerKeyDown()
  }

  componentWillUnmount() {
    if (this.onKeyDown) window.removeEventListener("keydown", this.onKeyDown)
  }

  on_new_state(new_s:s) {
    this.setState(s => ({...s, value:new_s}), () => this.props.on_done_s(new_s))
  }

  componentWillReceiveProps(nextProps:StatefulProps<s>) {
    if (this.props.options != undefined && this.props.options.mergeNewProps != undefined) {
      const mergeNewProps = this.props.options.mergeNewProps
      this.setState(s => ({...s, value:mergeNewProps(s.value, nextProps.input)}))
    }
  }

  render() {
    return <React.Fragment key={option("key", this.props.options)}>
        {this.props.widget_s(this.state.value).run(new_s => this.on_new_state(new_s))}
      </React.Fragment>
  }
}

/** It is a wrapper that takes an [[IOWidget]] and enhances it with the ability of having "memory", i.e. state.
 * 
 * A plain (stateless) [[IOWidget]] simply takes an input, renders something and produces some output (to possibly feed to other widgets).
 * The output is lost the next time the IOWidget is re-rendered, because the React app has been updated.
 * 
 * @param options 
 */
export const stateful = function<s>(options?:StateOptions<s>) : Action<IOWidget<s,s>> {
  return widget_s => (input:s) => mk_widget({
    run:(on_done_s:(output:s) => void) =>
      React.createElement<StatefulProps<s>>(Stateful, { on_done_s, widget_s, input, options })
  })
}
